import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { io } from "socket.io-client";
import { useAuth } from "./AuthContext";
import toast from "react-hot-toast";
import { CONFIG } from "../config";

const ChatContext = createContext(null);

const WS_URL = CONFIG.WS_URL;

export function ChatProvider({ children }) {
    const { user, updateUser } = useAuth(); // We need dynamic user/token from AuthContext
    const [socket, setSocket] = useState(null);
    const [connected, setConnected] = useState(false);
    const [onlineUsers, setOnlineUsers] = useState(new Map());

    // Store latest state in refs to avoid closure staleness in socket callbacks
    const socketRef = useRef(null);

    // 1. Establish Connection
    useEffect(() => {
        if (!user) {
            if (socketRef.current) {
                console.log("🔌 [ChatContext] No user, disconnecting...");
                socketRef.current.disconnect();
                socketRef.current = null;
                setSocket(null);
                setConnected(false);
            }
            return;
        }

        // If already connected with same token, skip? 
        // Socket.io handles this, but let's be safe.
        if (socketRef.current && socketRef.current.connected) {
            // Check if token changed? Implementation detail.
            // For now, simple approach: mount/unmount or token change triggers reconnect.
        }

        console.log("🔌 [ChatContext] Connecting...", { userId: user?.id, url: WS_URL });

        const newSocket = io(WS_URL, {
            withCredentials: true,
            reconnection: true,
            reconnectionAttempts: 10,
            reconnectionDelay: 2000,
            transports: ["websocket", "polling"], // Enforce robust transport
        });

        socketRef.current = newSocket;

        newSocket.on("connect", () => {
            console.log("✅ [ChatContext] Connected:", newSocket.id);
            setConnected(true);
            setSocket(newSocket);
        });

        newSocket.on("disconnect", (reason) => {
            console.log("❌ [ChatContext] Disconnected:", reason);
            setConnected(false);
            if (reason === "io server disconnect") {
                // Server kicked us (invalid token?), try reconnecting manually or re-login
                // newSocket.connect(); 
            }
        });

        newSocket.on("connect_error", (err) => {
            console.error("⚠️ [ChatContext] Error:", err.message);
        });

        // Global Listeners (Presence, Typing, etc.)
        newSocket.on("presence", ({ userId, online, timestamp }) => {
            setOnlineUsers(prev => {
                const next = new Map(prev);
                if (online) next.set(userId, timestamp || Date.now());
                else next.delete(userId);
                return next;
            });
        });

        // NEW: Global Toast Notification for background messages
        newSocket.on("receive_message", (msg) => {
            const isChatPage = window.location.pathname === "/chat";
            if (!isChatPage && msg.senderId !== user.id) {
                toast(`New message`, {
                    icon: '💬',
                    duration: 4000,
                    style: {
                        background: '#333',
                        color: '#fff',
                    },
                });
            }
        });

        return () => {
            console.log("🔌 [ChatContext] Cleanup...");
            newSocket.disconnect();
            socketRef.current = null;
            setSocket(null);
            setConnected(false);
        };
    }, [user?.id]); // Re-connect if user changes

    // 1b. Periodic Activity Ping
    useEffect(() => {
        if (!connected || !socketRef.current || !user?.id) return;

        let lastActivity = Date.now();
        const updateActivity = () => { lastActivity = Date.now(); };

        window.addEventListener('mousemove', updateActivity);
        window.addEventListener('keydown', updateActivity);
        window.addEventListener('scroll', updateActivity, true);
        window.addEventListener('click', updateActivity);

        const pingInterval = setInterval(() => {
            if (Date.now() - lastActivity < 60000) { // Active within 1 minute
                socketRef.current.emit("activity");
            }
        }, 30000); // Ping every 30 seconds

        socketRef.current.emit("activity"); // Initial ping

        return () => {
            window.removeEventListener('mousemove', updateActivity);
            window.removeEventListener('keydown', updateActivity);
            window.removeEventListener('scroll', updateActivity, true);
            window.removeEventListener('click', updateActivity);
            clearInterval(pingInterval);
        };
    }, [connected, user?.id]);

    // 1c. Stale User Pruning
    useEffect(() => {
        const pruneInterval = setInterval(() => {
            setOnlineUsers(prev => {
                let changed = false;
                const next = new Map(prev);
                const now = Date.now();
                for (let [uid, timestamp] of next.entries()) {
                    if (now - timestamp > 120000) { // Offline after 2 minutes
                        next.delete(uid);
                        changed = true;
                    }
                }
                return changed ? next : prev;
            });
        }, 15000);
        return () => clearInterval(pruneInterval);
    }, []);

    // 2. Poll for Token Changes (Silent Refresh Handling)
    useEffect(() => {
        // Cookies are sent automatically with the socket request now.
    }, []);


    // 3. Exposed Methods
    const joinConversation = useCallback((conversationId) => {
        if (!socketRef.current) {
            console.warn("⚠️ [ChatContext] Cannot join room: Socket not initialized");
            return;
        }
        if (!connected && socketRef.current.disconnected) {
            socketRef.current.connect();
        }
        console.log(`🔌 [ChatContext] Joining conversation: ${conversationId}`);
        socketRef.current.emit("join_conversation", { conversationId });
    }, [connected]);

    const sendMessage = useCallback(({ conversationId, content, type = "TEXT", isVent = false }) => {
        if (!socketRef.current) {
            console.error("❌ [ChatContext] sendMessage failed: Socket not initialized");
            toast.error("Connecting... please wait.");
            return;
        }
        if (!connected && socketRef.current.disconnected) {
            socketRef.current.connect();
        }
        console.log(`📤 [ChatContext] Sending message to ${conversationId}:`, content);
        socketRef.current.emit("send_message", { conversationId, content, type, isVent });
    }, [connected]);

    const typingEmitRef = useRef(0);
    const sendTyping = useCallback(({ conversationId, isTyping }) => {
        if (!socketRef.current) return;
        
        // 🛡️ THROTTLE: Only emit if it's a state change or if 3 seconds passed
        const now = Date.now();
        if (isTyping && now - typingEmitRef.current < 3000) return;
        
        typingEmitRef.current = now;
        socketRef.current.emit("typing", { conversationId, isTyping });
    }, []);

    const value = {
        socket,
        connected,
        onlineUsers,
        joinConversation,
        sendMessage,
        sendTyping,
        updateMood: (mood) => {
            if (socketRef.current) socketRef.current.emit("update_mood", { mood });
            if (updateUser) updateUser({ currentMood: mood });
        }
    };

    return (
        <ChatContext.Provider value={value}>
            {children}
        </ChatContext.Provider>
    );
}

export function useChatContext() {
    return useContext(ChatContext);
}
